import React from 'react';

import { Typography } from '@mui/material';
import { Apollo } from 'src/gql/generated';
import {
  useAreWorkflowStepsActiveOrComplete,
  useDropAction,
  useTimelineItemStyles,
  useTimelineStyles,
} from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const minHeightPx = 64;
const useStyles = makeStyles()(theme => ({
  emptyContent: {
    alignContent: 'center',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minHeight: minHeightPx,
    padding: theme.spacing(4),
  },
  missingActionOver: {
    backgroundColor: theme.palette.common.white,
    border: `1px dashed ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    minHeight: minHeightPx,
  },
}));

interface EmptyWorkflowSectionProps {
  content: string;
  workflowId: UUID;
  showProgress?: boolean;
}

export function EmptyWorkflowSection({
  content,
  showProgress = true,
  workflowId,
}: EmptyWorkflowSectionProps): JSX.Element {
  const { classes: timelineClasses } = useTimelineStyles();
  const isEmptySectionActiveOrComplete =
    useAreWorkflowStepsActiveOrComplete(workflowId) && showProgress;
  const { classes: timelineItemClasses } = useTimelineItemStyles({
    isActiveStep: isEmptySectionActiveOrComplete,
    isNumberedSection: false,
    isStepComplete: isEmptySectionActiveOrComplete,
  });
  const [{ isOver: isActionOver }, actionDropRef] = useDropAction({
    actionPlacement: Apollo.ActionPlacement.WithinStep,
    targetStepId: null,
    workflowId,
  });

  return (
    <div className={timelineClasses.timeline}>
      <div className={timelineItemClasses.timelineItem}>
        <div ref={actionDropRef} className={timelineItemClasses.timelineContent}>
          <EmptyWorkflowSectionContent content={content} isActionGroupOver={isActionOver} />
        </div>
      </div>
    </div>
  );
}

interface EmptyWorkflowSectionContentProps {
  content: string;
  isActionGroupOver: boolean;
}
function EmptyWorkflowSectionContent({
  content,
  isActionGroupOver,
}: EmptyWorkflowSectionContentProps): JSX.Element {
  const { classes } = useStyles();

  if (isActionGroupOver) return <div className={classes.missingActionOver} />;

  return (
    <div className={classes.emptyContent}>
      <Typography variant="body1">{content}</Typography>
    </div>
  );
}
